import { useMatch } from '@tanstack/react-router';
import { useCallback, useRef } from 'react';
import { useIsUnmountingRef } from '@/hooks/state/use-is-unmounting-ref';

/** The part of a location the check reads. */
type BlockedLocation = { pathname: string };

/** Whether `pathname` is `base` or a path beneath it, by whole segments. */
function isWithin(pathname: string, base: string): boolean {
  const trimmed = base.replace(/\/+$/, '');
  return trimmed === '' || pathname === trimmed || pathname.startsWith(`${trimmed}/`);
}

/**
 * For a router blocker (`useBlocker`): whether a navigation reaches it after its page was already
 * left, so it lets that navigation through instead of asking.
 *
 * That happens when the page arrived at makes a redirect as it renders (the bare `/broadcasts`
 * forwards to its first tab with a `<Navigate>`). The redirect fires in a layout effect, before the
 * leaving page's blocker unregisters in its passive cleanup. Blocked there, it waits on a prompt that
 * unmounts with the page, and the app is left on the redirect's empty route.
 *
 * Both have to hold: the page's layout effects are torn down, and the history has moved outside the
 * route the page renders under. The first alone is also true while a Suspense boundary hides the
 * page behind its fallback, which keeps the page and its unsaved changes; a hidden page is still
 * inside its own route, including a page such as the setup wizard that moves between paths beneath
 * it. Without a route around it, nothing counts as left.
 */
export function useHasLeftPage(): (current: BlockedLocation) => boolean {
  const isUnmounting = useIsUnmountingRef();
  const routePathname = useMatch({ strict: false, shouldThrow: false, select: (match) => match.pathname });
  const routePathnameRef = useRef(routePathname);
  routePathnameRef.current = routePathname;

  return useCallback(
    (current) => {
      const route = routePathnameRef.current;
      return isUnmounting.current && route != null && !isWithin(current.pathname, route);
    },
    [isUnmounting],
  );
}
